import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiClientError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Field, Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ErrorBanner } from '../components/Status';
import { ThemeToggle } from '../components/ThemeToggle';

/**
 * JEDNO POLE EMAIL (ROADMAP.md M1: „wylacznie magic link, bez hasel i OAuth").
 *
 * Nie ma osobnej rejestracji ani osobnego ekranu przyjecia zaproszenia:
 * `POST /api/auth/magic-link` odpowiada IDENTYCZNIE dla adresu znanego i
 * nieznanego, wiec ten ekran nie ujawnia, kto ma konto. Zaproszony czlonek
 * dostaje link tym samym mechanizmem i laduje w tym samym `/auth/callback`.
 */
export function SignInPage({ productName = 'GenAI Studio' }: { readonly productName?: string }) {
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const send = useMutation<unknown, ApiClientError, string>({
    mutationFn: (address: string) => api.auth.magicLink({ email: address }),
    onSuccess: (_data, address) => {
      setSentTo(address);
    },
  });

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 p-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{productName}</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Logowanie linkiem z maila. Bez hasla.
          </p>
        </div>
        <ThemeToggle />
      </header>

      {sentTo === null ? (
        <Card>
          <CardHeader>
            <CardTitle>Podaj email</CardTitle>
            <CardDescription>Wyslemy jednorazowy link wazny 15 minut.</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                const address = email.trim();
                if (address.length === 0) return;
                send.mutate(address);
              }}
            >
              <Field label="Email">
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    autoFocus
                    placeholder="ty@firma.pl"
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                  />
                )}
              </Field>

              <ErrorBanner error={send.error} />

              <Button type="submit" variant="brand" className="w-full" disabled={send.isPending}>
                {send.isPending ? 'Wysylam…' : 'Wyslij link'}
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Sprawdz skrzynke</CardTitle>
            <CardDescription>
              Jesli <span className="font-mono">{sentTo}</span> ma dostep, link juz tam jest.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-neutral-600 dark:text-neutral-300">
            <p>Link jest jednorazowy i wygasa po 15 minutach. Otworz go w tej przegladarce.</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSentTo(null);
                  send.reset();
                }}
              >
                Zmien adres
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={send.isPending}
                onClick={() => send.mutate(sentTo)}
              >
                Wyslij ponownie
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </main>
  );
}

export default SignInPage;
